import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Panel } from 'primereact/panel';
import FileState from '../../enums/FileState';
import WheelPhotoThumbnailGrid from './WheelPhotoThumbnailGrid';
import WheelFieldLabel from './WheelFieldLabel';
import WheelOptionTiles from './WheelOptionTiles';
import {
  WHEEL_INSURANCE_OPTIONS,
  WHEEL_POSITIONS,
  WHEEL_PHOTO_PART,
  WHEEL_PRE_PHOTOS_PER_POSITION,
  prePhotoPart,
} from '../../constants/WheelInspectionConstants';
import {
  getWheelInspectionFieldPath,
  getPositionLabel,
  applyLocationDetailDefaultsForPosition,
} from '../../utils/wheelInspectionUtil';

const WheelPreRestorationPanel = ({
  control,
  setValue,
  getValues,
  getFormErrorMessage,
  formRequired = false,
  fileItems,
  setFileItems,
  getFileURL,
  setLoading,
  readOnly = false,
}) => {
  const insuranceHandled = useWatch({
    control,
    name: getWheelInspectionFieldPath('preRestoration.insuranceHandled'),
  });

  const selectedPositions =
    useWatch({
      control,
      name: getWheelInspectionFieldPath('preRestoration.positions'),
    }) || [];

  const activePositionRows = WHEEL_POSITIONS.filter((p) =>
    selectedPositions.includes(p.code)
  );

  const togglePosition = (field, code, checked) => {
    const next = checked
      ? [...(field.value || []), code]
      : (field.value || []).filter((p) => p !== code);
    field.onChange(next);

    if (!checked && setFileItems) {
      const partCode = prePhotoPart(code);
      setFileItems((prev) =>
        prev
          .map((item) => {
            if (
              item.partCode === partCode &&
              item.state !== FileState.Deleted
            ) {
              if (item.photoId) {
                return { ...item, state: FileState.Deleted };
              }
              return null;
            }
            return item;
          })
          .filter(Boolean)
      );
    }
  };

  return (
    <Panel header="휠 복원 전 사진 기록" className="shadow-2">
      <div className="grid">
        <div className="col-12 md:col-8 mb-3">
          <WheelFieldLabel required={formRequired}>
            보험처리 여부
          </WheelFieldLabel>
          <Controller
            control={control}
            name={getWheelInspectionFieldPath('preRestoration.insuranceHandled')}
            defaultValue={[]}
            rules={
              formRequired
                ? {
                    validate: (value) =>
                      (value || []).length > 0 ||
                      '보험처리 여부를 선택해 주세요.',
                  }
                : undefined
            }
            render={({ field }) => (
              <>
                <WheelOptionTiles
                  type="checkbox"
                  options={WHEEL_INSURANCE_OPTIONS}
                  value={field.value || []}
                  onChange={field.onChange}
                  disabled={readOnly}
                  tileClassName="wheel-option-tile--wide"
                />
                {getFormErrorMessage?.(field.name)}
              </>
            )}
          />
        </div>

        <div className="col-12 mb-3">
          <WheelFieldLabel>위치</WheelFieldLabel>
          <p className="text-sm text-color-secondary mb-2">
            선택한 위치별로 복원 전 사진을 등록할 수 있으며, 동일 위치가 휠
            안전 검사·위치별 상세 점검에 적용됩니다.
          </p>
          <Controller
            control={control}
            name={getWheelInspectionFieldPath('preRestoration.positions')}
            defaultValue={[]}
            render={({ field }) => (
              <WheelOptionTiles
                type="checkbox"
                options={WHEEL_POSITIONS.map(({ code, label }) => ({
                  value: code,
                  label,
                }))}
                value={field.value}
                onChange={(next) => {
                  const prev = field.value || [];
                  const added = next.filter((c) => !prev.includes(c));
                  const removed = prev.filter((c) => !next.includes(c));
                  field.onChange(next);
                  added.forEach((code) =>
                    applyLocationDetailDefaultsForPosition(setValue, code, getValues)
                  );
                  removed.forEach((code) => togglePosition(field, code, false));
                }}
                disabled={readOnly}
                tileClassName="wheel-option-tile--wide"
              />
            )}
          />
        </div>

        {(insuranceHandled || []).includes('Y') && (
          <div className="col-12 md:col-6 mb-3">
            <WheelFieldLabel required={formRequired}>
              차량번호(번호판) 사진
            </WheelFieldLabel>
            <p className="text-sm text-color-secondary mb-2">
              보험처리 시 번호판 사진이 필요합니다. 썸네일 영역을 클릭해
              등록하세요.
            </p>
            <WheelPhotoThumbnailGrid
              partCode={WHEEL_PHOTO_PART.PRE_PLATE}
              fileItems={fileItems}
              setFileItems={setFileItems}
              getFileURL={getFileURL}
              maxFiles={1}
              multiple={false}
              disabled={readOnly}
              setLoading={setLoading}
            />
          </div>
        )}

        {activePositionRows.length > 0 && (
          <div className="col-12 mb-2">
            <WheelFieldLabel>복원 전 사진</WheelFieldLabel>
            <p className="text-sm text-color-secondary mb-2">
              썸네일 영역을 클릭해 위치별 사진을 1장씩 등록하세요. (선택)
            </p>
            <div className="flex flex-wrap gap-3 align-items-end">
              {activePositionRows.map(({ code }) => (
                <div
                  key={code}
                  className="flex flex-column align-items-center gap-1"
                >
                  <span className="text-sm font-semibold white-space-nowrap">
                    {getPositionLabel(code)}
                  </span>
                  <WheelPhotoThumbnailGrid
                    partCode={prePhotoPart(code)}
                    fileItems={fileItems}
                    setFileItems={setFileItems}
                    getFileURL={getFileURL}
                    maxFiles={WHEEL_PRE_PHOTOS_PER_POSITION}
                    multiple={false}
                    disabled={readOnly}
                    setLoading={setLoading}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedPositions.length === 0 && (
          <div className="col-12">
            <p className="text-color-secondary m-0">
              위치를 선택하면 복원 전 사진 등록 영역이 표시됩니다.
            </p>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default WheelPreRestorationPanel;
